import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { THEMES, DEFAULT_THEME, THEME_STORAGE_KEY, isThemeId, ThemeId } from './themeRegistry';
import { ThemeCenter } from '../components/ThemeCenter';

interface ThemeContextValue {
  theme: ThemeId;
  setTheme: (id: ThemeId) => void;
  themes: typeof THEMES;
  themeCenterOpen: boolean;
  openThemeCenter: () => void;
  closeThemeCenter: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/** 把主题写到 <html> 上：data-theme 驱动 token 换肤，data-mode 驱动明暗 */
function applyTheme(id: ThemeId) {
  if (typeof document === 'undefined') return;
  const meta = THEMES.find((t) => t.id === id) ?? THEMES[0];
  const root = document.documentElement;
  root.setAttribute('data-theme', id);
  root.setAttribute('data-mode', meta.light ? 'light' : 'dark');
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeId>(() => {
    try {
      const saved = localStorage.getItem(THEME_STORAGE_KEY);
      return isThemeId(saved) ? saved : DEFAULT_THEME;
    } catch {
      return DEFAULT_THEME;
    }
  });
  const [themeCenterOpen, setThemeCenterOpen] = useState(false);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = useCallback((id: ThemeId) => {
    setThemeState(id);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, id);
    } catch {
      /* ignore quota / privacy mode */
    }
  }, []);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        setTheme,
        themes: THEMES,
        themeCenterOpen,
        openThemeCenter: () => setThemeCenterOpen(true),
        closeThemeCenter: () => setThemeCenterOpen(false),
      }}
    >
      {children}
      <ThemeCenter
        open={themeCenterOpen}
        onClose={() => setThemeCenterOpen(false)}
        current={theme}
        onSelect={setTheme}
      />
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme 必须在 <ThemeProvider> 内使用');
  return ctx;
}
